import type { MantineColorScheme, MantineColorSchemeManager } from "@mantine/core";

import { CookieName } from "./cookie";

const COLOR_SCHEME_EVENT = "mantine-color-scheme-change";
const DEFAULT_COLOR_SCHEME: MantineColorScheme = "light";

const isColorScheme = (
  value?: string | null,
): value is MantineColorScheme => {
  return value === "light" || value === "dark" || value === "auto";
};

const extractCookieValue = (cookies?: string | null) => {
  if (!cookies) return null;

  return cookies
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${CookieName.COLOR_SCHEME}=`))
    ?.split("=")
    ?.slice(1)
    ?.join("=");
};

const sanitizeColorScheme = (
  value?: string | null,
  fallback: MantineColorScheme = DEFAULT_COLOR_SCHEME,
): MantineColorScheme => {
  if (isColorScheme(value)) {
    return value;
  }

  return fallback;
};

export const getColorSchemeFromCookies = (
  cookies?: string | null,
  fallback: MantineColorScheme = DEFAULT_COLOR_SCHEME,
): MantineColorScheme => {
  const storedValue = extractCookieValue(cookies);
  return sanitizeColorScheme(storedValue, fallback);
};

export const createCookieColorSchemeManager = (
  cookies?: string | null,
): MantineColorSchemeManager => {
  let unsubscribeHandler: (() => void) | undefined;

  const readScheme = (fallback: MantineColorScheme) => {
    if (typeof document === "undefined") {
      return getColorSchemeFromCookies(cookies, fallback);
    }

    return getColorSchemeFromCookies(document.cookie, fallback);
  };

  return {
    get: (defaultValue) => readScheme(defaultValue),
    set: (value) => {
      if (typeof document === "undefined") return;

      // Persist the scheme in a cookie for SSR consistency
      document.cookie = `${CookieName.COLOR_SCHEME}=${value}; path=/; max-age=${
        60 * 60 * 24 * 365
      }; SameSite=Lax`;

      // Notify subscribers (used internally by Mantine)
      window.dispatchEvent(
        new CustomEvent(COLOR_SCHEME_EVENT, { detail: value }),
      );
    },
    subscribe: (onUpdate) => {
      if (typeof window === "undefined") return;

      const handler = (event: Event) => {
        if (
          event instanceof CustomEvent &&
          isColorScheme(event.detail as string)
        ) {
          onUpdate(event.detail as MantineColorScheme);
        }
      };

      window.addEventListener(
        COLOR_SCHEME_EVENT,
        handler as unknown as EventListener,
      );
      unsubscribeHandler = () =>
        window.removeEventListener(
          COLOR_SCHEME_EVENT,
          handler as unknown as EventListener,
        );
    },
    unsubscribe: () => {
      unsubscribeHandler?.();
    },
    clear: () => {
      if (typeof document === "undefined") return;
      document.cookie = `${CookieName.COLOR_SCHEME}=; path=/; max-age=0; SameSite=Lax`;
    },
  };
};

export const getColorSchemeScript = (
  fallback: MantineColorScheme = DEFAULT_COLOR_SCHEME,
) => {
  const safeFallback = sanitizeColorScheme(fallback);
  const cookieKey = `${CookieName.COLOR_SCHEME}=`;
  const fallbackValue = JSON.stringify(safeFallback);
  const cookieKeyJson = JSON.stringify(cookieKey);

  return `(function(){try{var match=document.cookie.split('; ').find(function(row){return row.startsWith(${cookieKeyJson});});var stored=match?decodeURIComponent(match.split('=').slice(1).join('=')):${fallbackValue};var value=(stored==='light'||stored==='dark'||stored==='auto')?stored:${fallbackValue};var resolved=value!=='auto'?value:(window.matchMedia('(prefers-color-scheme: dark)').matches?'dark':'light');document.documentElement.setAttribute('data-mantine-color-scheme',resolved);}catch(e){document.documentElement.setAttribute('data-mantine-color-scheme',${fallbackValue});}})();`;
};
